// src/pages/CourseTime/hooks/useSlideNavigation.js
import { useState, useEffect, useRef } from 'react';

export const useSlideNavigation = (
  sessionContent,
  activeSegment,
  activeSessionNo,
  currentSlideIndex,
  setCurrentSlideIndex,
  updateCurrentPosition,
  resetActivityState,
  markSessionCompleted,
  programs,
  // Activity validation functions
  isActivityRequired,
  activityCompleted,
  showNotification,
  // Assessment submission handler
  onBeforeNavigate,
  // Segment completion popup control
  setShowSegmentCompletionPopup,
  // Ref recording which session the loaded sessionContent belongs to
  loadedSessionContentRef,
  // True while a session's content and position are still being loaded
  sessionLoading
) => {
  const [collapsed, setCollapsed] = useState(false);
  
  // Active session's hierarchy (always reflects current session)
  const [activeExpandedProgram, setActiveExpandedProgram] = useState(null);
  const [activeExpandedUnit, setActiveExpandedUnit] = useState(null);
  const [activeExpandedSession, setActiveExpandedSession] = useState(null);
  
  // User exploration hierarchy (independent expansion for browsing)
  const [explorationExpandedPrograms, setExplorationExpandedPrograms] = useState(new Set());
  const [explorationExpandedUnits, setExplorationExpandedUnits] = useState(new Set());
  
  const endOfSegmentRef = useRef(null);

  const toggleSider = () => {
    setCollapsed(!collapsed);
  };

  // Helper to find and expand parent containers for active session
  const expandParentContainers = (sessionNo) => {
    if (sessionNo === activeExpandedSession) {
      return;
    }

    for (const program of programs) {
      for (const unit of program.units || []) {
        for (const session of unit.sessions || []) {
          if (session.session_no === sessionNo) {
            // Found the session, update active hierarchy
            setActiveExpandedProgram(program.code);
            setActiveExpandedUnit(unit.unit_no);
            setActiveExpandedSession(sessionNo);
            return;
          }
        }
      }
    }
  };

  // Helper to check if a program/unit should be expanded
  const isProgramExpanded = (programCode) => {
    // Check both exploration state AND active state
    return explorationExpandedPrograms.has(programCode) || programCode === activeExpandedProgram;
  };

  const isUnitExpanded = (unitNo) => {
    // Check both exploration state AND active state
    return explorationExpandedUnits.has(unitNo) || unitNo === activeExpandedUnit;
  };

  const isSessionExpanded = (sessionNo) => activeExpandedSession === sessionNo;

  // Sidebar event handlers for exploration
  const handleToggleProgram = (programCode) => {
    setExplorationExpandedPrograms(prev => {
      const newSet = new Set(prev);

      if (newSet.has(programCode)) {
        // Collapse if already expanded
        newSet.delete(programCode);
        setActiveExpandedProgram(null);
        setActiveExpandedUnit(null);
      } else {
        newSet.add(programCode);
        setActiveExpandedProgram(programCode);
      }
      return newSet;
    });
  };

  const handleToggleUnit = (unitNo) => {
    setExplorationExpandedUnits(prev => {
      const newSet = new Set(prev);
      if (newSet.has(unitNo)) {
        newSet.delete(unitNo);
        setActiveExpandedUnit(null);
      } else {
        newSet.add(unitNo);
        setActiveExpandedUnit(unitNo);
      }
      return newSet;
    });
  };

  const handleToggleSession = (sessionNo) => {
    // If clicking the currently active session, just collapse it
    if (activeExpandedSession === sessionNo) {
      console.log('Collapsing session');
      setActiveExpandedSession(null);
    } else {
      console.log('Expanding session');
      // Find parent program + unit for the new session
      for (const program of programs) {
        for (const unit of program.units || []) {
          if ((unit.sessions || []).some(s => s.session_no === sessionNo)) {
            // Update active hierarchy
            setActiveExpandedProgram(program.code);
            setActiveExpandedUnit(unit.unit_no);
            setActiveExpandedSession(sessionNo);
            break;
          }
        }
      }
    }
    return sessionNo;
  };

  // Clear exploration expansions when new session is selected
  const clearExplorationExpansions = () => {
    setExplorationExpandedPrograms(new Set());
    setExplorationExpandedUnits(new Set());
  };



  const goToNextSlide = async () => {
    if (!sessionContent || !activeSegment || !Array.isArray(sessionContent[activeSegment])) {
      return;
    }
    
    // Get current slide to check if it's a required activity
    const currentSlide = sessionContent[activeSegment][currentSlideIndex];
    
    // Check if we're on the last slide of the segment
    const isLastSlideOfCurrentSegment = currentSlideIndex === sessionContent[activeSegment].length - 1;
    
    if (isLastSlideOfCurrentSegment) {
      // This is the last slide of the segment
      
      // For assessment activities, handle submission before showing popup
      if (currentSlide.SlideLayout === 'ASSESSMENT_ACTIVITY') {
        if (onBeforeNavigate) {
          const canNavigate = await onBeforeNavigate(currentSlide);
          if (canNavigate === false) {
            return;
          }
        }
      } else {
        // For other required activities, check if completed BEFORE showing popup
        if (isActivityRequired && isActivityRequired(currentSlide)) {
          const isCompleted = activityCompleted && activityCompleted[currentSlide.SlideID];
          if (!isCompleted) {
            if (showNotification) {
              showNotification('Please complete this required activity before proceeding.');
            }
            return;
          }
        }
      }
      
      // All checks passed, show popup
      if (setShowSegmentCompletionPopup) {
        setShowSegmentCompletionPopup(true);
      }
      return;
    }
    
    // Not the last slide, proceed normally
    if (currentSlideIndex < sessionContent[activeSegment].length - 1) {
      // For assessment activities, handle submission before navigation
      if (currentSlide.SlideLayout === 'ASSESSMENT_ACTIVITY') {
        if (onBeforeNavigate) {
          const canNavigate = await onBeforeNavigate(currentSlide);
          if (canNavigate === false) {
            return;
          }
        }
      } else {
        // For other required activities (poll, journaling, etc.), check if completed
        if (isActivityRequired && isActivityRequired(currentSlide)) {
          const isCompleted = activityCompleted && activityCompleted[currentSlide.SlideID];
          if (!isCompleted) {
            if (showNotification) {
              showNotification('Please complete this required activity before proceeding to the next slide.');
            }
            return;
          }
        }
      }

      const nextSlideIndex = currentSlideIndex + 1;
      setCurrentSlideIndex(nextSlideIndex);
      resetActivityState();

      // Check if this will be the last slide of the segment
      const willBeLastSlide = nextSlideIndex === sessionContent[activeSegment].length - 1;
      
      // Only immediate update for last slide of segment/session
      if (willBeLastSlide) {
        updateCurrentPosition(activeSessionNo, activeSegment, nextSlideIndex, true);
      }
    }
  };

  const goToPrevSlide = () => {
    if (currentSlideIndex > 0) {
      // Hide segment completion popup if showing
      if (setShowSegmentCompletionPopup) {
        setShowSegmentCompletionPopup(false);
      }
      
      const prevSlideIndex = currentSlideIndex - 1;
      setCurrentSlideIndex(prevSlideIndex);
      resetActivityState();
    }
  };

  const findNextSession = () => {
    let foundCurrent = false;
    let foundInUnit = false;
    let currentUnitIndex = -1;
    let currentProgramIndex = -1;

    // First, find current program and unit indices
    for (let p = 0; p < programs.length; p++) {
      const program = programs[p];
      if (foundCurrent) break;

      for (let u = 0; u < (program.units || []).length; u++) {
        const unit = program.units[u];
        if (foundCurrent) break;

        for (let i = 0; i < (unit.sessions || []).length; i++) {
          const session = unit.sessions[i];
          if (session.session_no === activeSessionNo) {
            foundCurrent = true;
            foundInUnit = true;
            currentUnitIndex = u;
            currentProgramIndex = p;

            // Check if there's another session in this unit
            if (i < unit.sessions.length - 1) {
              return unit.sessions[i + 1];
            }
            break;
          }
        }
      }
    }

    // If found the current session but no next in the same unit
    if (foundCurrent && foundInUnit) {
      const currentProgram = programs[currentProgramIndex];

      // Check next unit in same program
      if (currentUnitIndex < currentProgram.units.length - 1) {
        const nextUnit = currentProgram.units[currentUnitIndex + 1];
        if (nextUnit.sessions && nextUnit.sessions.length > 0) {
          return nextUnit.sessions[0];
        }
      }

      // Check next program
      if (currentProgramIndex < programs.length - 1) {
        const nextProgram = programs[currentProgramIndex + 1];
        if (nextProgram.units &&
            nextProgram.units.length > 0 &&
            nextProgram.units[0].sessions &&
            nextProgram.units[0].sessions.length > 0) {
          return nextProgram.units[0].sessions[0];
        }
      }
    }

    // No next session found - end of course
    return null;
  };

  const findSessionName = () => {
    for (const program of programs) {
      for (const unit of program.units || []) {
        for (const session of unit.sessions || []) {
          if (session.session_no === activeSessionNo) {
            return session.full_name;
          }
        }
      }
    }
    return 'Session';
  };

  // Helper function to check if current position is the last slide of session/course
  const checkLastSlideStatus = () => {
    if (!sessionContent || !activeSegment) return { isLastSlideOfSegment: false, isLastSegment: false, isLastSession: false };
    
    const segmentSlides = sessionContent[activeSegment] || [];
    const isLastSlideOfSegment = currentSlideIndex === segmentSlides.length - 1;
    
    // Only real segments are arrays of slides — metadata fields like `tier` live at the same
    // top level and must not become a pseudo-segment (its lone value would render as a fake slide).
    const segmentKeys = Object.keys(sessionContent).filter(segmentKey => Array.isArray(sessionContent[segmentKey]));
    const currentSegmentIndex = segmentKeys.indexOf(activeSegment);
    const isLastSegment = currentSegmentIndex === segmentKeys.length - 1;
    
    // Check if current session is the last session in the course
    const isLastSession = !findNextSession();
    
    return { isLastSlideOfSegment, isLastSegment, isLastSession };
  };

  // Auto-complete session when reaching last slide
  useEffect(() => {
    // Guard against the session-switch race: when navigating to another session/
    // module, activeSessionNo updates before sessionContent (async fetch), so the
    // new session is briefly paired with the previous session's last-slide position.
    // Only auto-complete when the loaded content actually belongs to the active
    // session, otherwise the freshly-opened next session/module gets marked complete.
    // The content can also arrive before the segment/slide position is reset, so
    // wait until the session has finished loading; the effect re-runs when it does.
    if (sessionLoading || (loadedSessionContentRef && loadedSessionContentRef.current !== activeSessionNo)) {
      return;
    }

    if (activeSessionNo && activeSegment && sessionContent && currentSlideIndex >= 0) {
      const { isLastSlideOfSegment, isLastSegment, isLastSession } = checkLastSlideStatus();
      
      // Mark session as completed when user lands on the last slide of the session
      // BUT: Don't mark the LAST session - that's handled server-side by finishCourseAssessments
      if (isLastSlideOfSegment && isLastSegment && !isLastSession) {
        // Immediate update for reaching end of session
        updateCurrentPosition(activeSessionNo, activeSegment, currentSlideIndex, true);
        
        // Mark session as completed - capture sessionNo to avoid race condition
        const sessionToComplete = activeSessionNo;
        setTimeout(() => {
          markSessionCompleted(sessionToComplete);
        }, 200); // Small delay to ensure position is saved first
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionNo, activeSegment, currentSlideIndex, sessionContent, sessionLoading]);

  // Scroll to end of segment when reaching last slide
  useEffect(() => {
    if (
      sessionContent &&
      activeSegment &&
      Array.isArray(sessionContent[activeSegment]) &&
      currentSlideIndex === sessionContent[activeSegment].length - 1
    ) {
      setTimeout(() => {
        if (endOfSegmentRef.current) {
          endOfSegmentRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 200);
    }
  }, [currentSlideIndex, activeSegment, sessionContent]);

  return {
    // State
    collapsed,
    
    // Expansion state helpers
    isProgramExpanded,
    isUnitExpanded, 
    isSessionExpanded,
    
    // Functions
    toggleSider,
    expandParentContainers,
    clearExplorationExpansions,
    handleToggleProgram,
    handleToggleUnit,
    handleToggleSession,
    goToNextSlide,
    goToPrevSlide,
    findNextSession,
    findSessionName,
    checkLastSlideStatus,
    
    // Refs
    endOfSegmentRef
  };
};
